import { useSession } from 'next-auth/react';
import { Prisma } from '@prisma/client';
import { fetchWithToken } from '../../helpers/fetchWithToken';
import { useQuery, useMutation } from '@tanstack/react-query';

const campaignWithTeam = Prisma.validator<Prisma.CampaignArgs>()({
  include: { team: true },
});

export type CampaignWithTeam = Prisma.CampaignGetPayload<
  typeof campaignWithTeam
>;

export default function useCampaign(
  slug: string,
  teamSlug: string,
): {
  campaign: CampaignWithTeam | null;
  isLoading: boolean;
  isError: boolean;
} {
  const { data: session } = useSession();

  const query = useQuery<CampaignWithTeam>(
    [teamSlug, slug],
    () =>
      fetchWithToken(
        `/campaigns?slug=${slug}&teamSlug=${teamSlug}`,
        session?.accessToken as string,
      ),
    {
      enabled: !!session?.accessToken,
      retry: false,
    },
  );

  return {
    campaign: query.data,
    isLoading: query.isLoading || !session?.accessToken,
    isError: query.isError,
  };
}

export function useCreateCampaignMutation() {
  const { data: session } = useSession();

  const mutation = useMutation(
    (newCampaign: Prisma.CampaignCreateInput) =>
      fetchWithToken('/campaigns', session?.accessToken as string, {
        method: 'POST',
        data: newCampaign,
      }),
    {
      // // When mutate is called:
      // onMutate: async (newCampaign: Prisma.CampaignCreateInput) => {
      //   // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      //   await queryClient.cancelQueries('campaigns');
      //   // Snapshot the previous value
      //   const previousCampaigns =
      //     queryClient.getQueryData<Campaign[]>('campaigns');
      //   // Optimistically update to the new value
      //   if (previousCampaigns) {
      //     queryClient.setQueryData<Campaign[]>('campaigns', {
      //       ...previousCampaigns,
      //       ...newCampaign,
      //     });
      //   }
      //   return { previousCampaigns };
      // },
      // // If the mutation fails, use the context returned from onMutate to roll back
      // onError: (err, variables, context) => {
      //   if (context?.previousCampaigns) {
      //     queryClient.setQueryData<Campaign[]>(
      //       'campaigns',
      //       context.previousCampaigns
      //     );
      //   }
      // },
      // // Always refetch after error or success:
      // onSettled: () => {
      //   queryClient.invalidateQueries('campaigns');
      // },
    },
  );

  return mutation;
}

export function useCampaignResetMutation() {
  const { data: session } = useSession();

  const mutation = useMutation((data: { campaignId: string }) =>
    fetchWithToken(
      `/campaigns/${data.campaignId}/reset`,
      session?.accessToken as string,
      {
        method: 'PATCH',
      },
    ),
  );
  return mutation;
}

export function useCampaignReloadMutation() {
  const { data: session } = useSession();

  const mutation = useMutation((data: { campaignId: string }) =>
    fetchWithToken(
      `/campaigns/${data.campaignId}/reload`,
      session?.accessToken as string,
      {
        method: 'PATCH',
      },
    ),
  );
  return mutation;
}
